/**
 * View principal de pipelines segmentadas — layout estilo Kommo CRM
 * Usado em /vendas/pipeline
 */

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AutoComplete, Empty, Select, Spin, Segmented, Drawer, Tooltip } from 'antd';
import { ChevronDown, LayoutGrid, List, Settings2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { formatDate } from '@/lib/utils';
import { useAuth } from '@/features/auth';
import { useProductionPeriodFilter } from '@/hooks/useProductionPeriodFilter';
import { useProductionStartDay } from '@/hooks/useProductionStartDay';
import { getRecentProductionPeriods } from '@/utils/productionPeriod';
import { usePipelinesList } from '../hooks/usePipelinesList';
import { canManagePipelineStages } from '../hooks/usePipelineStages';
import { usePipelineTags } from '../hooks/usePipelineTags';
import { useTenants } from '../hooks/useTenants';
import { useAssembleiaVencimentos } from '../hooks/useAssembleiaVencimentos';
import { usePipelineBoard } from '../hooks/usePipelineBoard';
import { buildGrupoCotaOptions } from '../utils/grupoCotaFilter';
import { PIPELINE_URL_PARAM, LEAD_URL_PARAM } from '../hooks/usePipelineLeadUrl';
import { Pipeline } from '../types';
import { PipelinePopover } from './PipelinePopover';
import { PipelineStagesEditor } from './PipelineStagesEditor';
import { PipelineEditModal } from './PipelineEditModal';
import { PipelineBoardKanban } from './PipelineBoardKanban';
import { PipelineListView } from './PipelineListView';
import { PipelineNewCardModal } from './PipelineNewCardModal';
import {
  PipelineFilterBar,
  PipelineBoardView,
  PipelineFilterSummary,
  PipelineActiveFilter,
} from './PipelineFilterBar';

const POPOVER_PORTAL_SELECTORS = [
  '.ant-select-dropdown',
  '.ant-popover',
  '.ant-picker-dropdown',
  '.ant-modal-root',
  '.ant-drawer',
].join(', ');

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      {children}
    </div>
  );
}

/** Rótulo do chip de filtro ativo: "Tag: VIP" ou "Tag: VIP +2" */
function chipLabel(prefix: string, values: string[]): string {
  const [first, ...rest] = values;
  return rest.length > 0 ? `${prefix}: ${first} +${rest.length}` : `${prefix}: ${first}`;
}

const filterOptionContains = (input: string, option?: { value?: unknown }) =>
  String(option?.value ?? '').toLowerCase().includes(input.trim().toLowerCase());

export function SegmentedPipelineView() {
  const { profile } = useAuth();
  const canManageStages = canManagePipelineStages(profile?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const { productionPeriodFilter, setProductionPeriodFilter } = useProductionPeriodFilter();
  const startDay = useProductionStartDay();
  // Cobrir contratos migrados desde 2024 (período de produção dia 21)
  const recentPeriods = getRecentProductionPeriods(36, startDay);
  const { pipelines, loading, error } = usePipelinesList();
  const { tags: tenantTags } = usePipelineTags();
  const { data: tenants = [] } = useTenants();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [tenantFilter, setTenantFilter] = useState<number[]>([]);
  const [assembleiaVencimentos, setAssembleiaVencimentos] = useState<string[]>([]);
  const [grupoFilter, setGrupoFilter] = useState('');
  const [cotaFilter, setCotaFilter] = useState('');
  const [boardView, setBoardView] = useState<PipelineBoardView>('ativos');
  const [summary, setSummary] = useState<PipelineFilterSummary | null>(null);
  const [viewMode, setViewMode] = useState<'kanban' | 'lista'>('kanban');
  const [newCardModalOpen, setNewCardModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const pipelineFromUrl = searchParams.get(PIPELINE_URL_PARAM);

  useEffect(() => {
    if (pipelines.length === 0) return;

    if (pipelineFromUrl && pipelines.some((p) => p.id === pipelineFromUrl)) {
      setSelectedId(pipelineFromUrl);
      return;
    }

    if (!selectedId) {
      setSelectedId(pipelines[0].id);
    } else if (!pipelines.find((p) => p.id === selectedId)) {
      setSelectedId(pipelines[0]?.id ?? null);
    }
  }, [pipelines, pipelineFromUrl, selectedId]);

  useEffect(() => {
    setBoardView('ativos');
    setSummary(null);
    setSearch('');
    setTenantFilter([]);
    setAssembleiaVencimentos([]);
    setGrupoFilter('');
    setCotaFilter('');
  }, [selectedId]);

  useEffect(() => {
    if (!popoverOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (popoverRef.current?.contains(target)) return;
      if (target.closest('[data-page-header-title-trigger]')) return;
      if (target.closest(POPOVER_PORTAL_SELECTORS)) return;
      setPopoverOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [popoverOpen]);

  const handleSelectPipeline = (id: string) => {
    setSelectedId(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(PIPELINE_URL_PARAM, id);
        next.delete(LEAD_URL_PARAM);
        return next;
      },
      { replace: true }
    );
  };

  const handleSummaryChange = useCallback((next: PipelineFilterSummary) => {
    setSummary((prev) => {
      if (prev && prev.count === next.count && prev.value === next.value) {
        return prev;
      }
      return next;
    });
  }, []);

  const selected = pipelines.find((p) => p.id === selectedId);
  const firstStage = selected?.stages?.[0];
  const isPosVenda = selected?.tipo === 'pos_venda';

  const { data: vencimentosAssembleia = [] } = useAssembleiaVencimentos(
    isPosVenda,
    productionPeriodFilter,
    startDay
  );

  // Remove seleções de vencimento que saíram do período de produção atual
  useEffect(() => {
    if (!isPosVenda) return;
    setAssembleiaVencimentos((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.filter((data) => vencimentosAssembleia.includes(data));
      return next.length === prev.length ? prev : next;
    });
  }, [isPosVenda, vencimentosAssembleia]);

  // Mesma query key do Kanban/Lista: reaproveita o cache, sem consulta extra
  const { kanbanBoard: posVendaBoard } = usePipelineBoard(
    isPosVenda && selectedId ? selectedId : '',
    productionPeriodFilter,
    startDay
  );

  const { grupoOptions, cotaOptions } = useMemo(
    () => buildGrupoCotaOptions(isPosVenda ? posVendaBoard : undefined),
    [isPosVenda, posVendaBoard]
  );

  const tenantOptions = useMemo(() => {
    if (!selected?.tenant_ids || selected.tenant_ids.length === 0) return tenants;
    return tenants.filter((t) => selected.tenant_ids!.includes(t.id));
  }, [tenants, selected?.tenant_ids]);

  const assembleiaOptions = useMemo(
    () => vencimentosAssembleia.map((data) => ({ value: data, label: formatDate(data) })),
    [vencimentosAssembleia]
  );

  // Assembleia é filtro opcional. O período de produção filtra via data_criacao no board.
  // Select vazio = todos os cards do período; com seleção = só essas datas de vencimento.
  const effectiveAssembleiaVencimentos = assembleiaVencimentos;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <p className="text-red-600 text-sm">{error.message}</p>
      </div>
    );
  }

  const productionPeriodValue = productionPeriodFilter
    ? `${productionPeriodFilter.year}-${String(productionPeriodFilter.month).padStart(2, '0')}`
    : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

  const handleProductionPeriodChange = (value: string) => {
    const [year, month] = value.split('-');
    setProductionPeriodFilter({ month: parseInt(month, 10), year: parseInt(year, 10) });
  };

  const clearFilters = () => {
    setTagFilter([]);
    setTenantFilter([]);
    setAssembleiaVencimentos([]);
    setGrupoFilter('');
    setCotaFilter('');
  };

  const activeFilters: PipelineActiveFilter[] = [];
  if (tagFilter.length > 0) {
    activeFilters.push({
      key: 'tag',
      label: chipLabel(
        'Tag',
        tagFilter.map((id) => tenantTags.find((t) => t.id === id)?.nome ?? '—')
      ),
      onRemove: () => setTagFilter([]),
    });
  }
  if (isPosVenda) {
    if (tenantFilter.length > 0) {
      activeFilters.push({
        key: 'tenant',
        label: chipLabel(
          'Representação',
          tenantFilter.map((id) => tenants.find((t) => t.id === id)?.nome ?? '—')
        ),
        onRemove: () => setTenantFilter([]),
      });
    }
    if (assembleiaVencimentos.length > 0) {
      activeFilters.push({
        key: 'assembleia',
        label: chipLabel('Vencimento', assembleiaVencimentos.map((data) => formatDate(data))),
        onRemove: () => setAssembleiaVencimentos([]),
      });
    }
    if (grupoFilter.trim()) {
      activeFilters.push({
        key: 'grupo',
        label: `Grupo: ${grupoFilter.trim()}`,
        onRemove: () => setGrupoFilter(''),
      });
    }
    if (cotaFilter.trim()) {
      activeFilters.push({
        key: 'cota',
        label: `Cota: ${cotaFilter.trim()}`,
        onRemove: () => setCotaFilter(''),
      });
    }
  }

  const filterFields = (
    <>
      <FilterField label="Tag">
        <Select
          mode="multiple"
          placeholder="Todas as tags"
          value={tagFilter}
          onChange={setTagFilter}
          options={tenantTags.map((t) => ({ value: t.id, label: t.nome }))}
          optionFilterProp="label"
          maxTagCount="responsive"
          allowClear
          className="w-full"
        />
      </FilterField>
      {isPosVenda && (
        <>
          <FilterField label="Representação">
            <Select
              mode="multiple"
              placeholder="Todas as representações"
              value={tenantFilter}
              onChange={setTenantFilter}
              options={tenantOptions.map((t) => ({ value: t.id, label: t.nome }))}
              optionFilterProp="label"
              maxTagCount="responsive"
              allowClear
              className="w-full"
            />
          </FilterField>
          <FilterField label="Vencimento da assembleia">
            <Select
              mode="multiple"
              placeholder="Todos os vencimentos"
              value={assembleiaVencimentos}
              onChange={setAssembleiaVencimentos}
              options={assembleiaOptions}
              maxTagCount="responsive"
              allowClear
              className="w-full"
            />
          </FilterField>
          <div className="grid grid-cols-2 gap-3">
            <FilterField label="Grupo">
              <AutoComplete
                placeholder="Ex.: 2068"
                value={grupoFilter}
                onChange={(value) => setGrupoFilter(value ?? '')}
                options={grupoOptions}
                filterOption={filterOptionContains}
                allowClear
                className="w-full"
              />
            </FilterField>
            <FilterField label="Cota">
              <AutoComplete
                placeholder="Ex.: 327"
                value={cotaFilter}
                onChange={(value) => setCotaFilter(value ?? '')}
                options={cotaOptions}
                filterOption={filterOptionContains}
                allowClear
                className="w-full"
              />
            </FilterField>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#f5f6f8] dark:bg-[#141414]">
      <div className="relative">
        <PageHeader
          heroic={true}
          title={selected?.nome || 'Pipeline'}
          heroLabel="Funil de Vendas"
          onTitleClick={() => setPopoverOpen((v) => !v)}
          titleIcon={
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${
                popoverOpen ? 'rotate-180' : ''
              }`}
            />
          }
          productionPeriod={{
            value: productionPeriodValue,
            options: recentPeriods.map((p) => ({
              label: p.label.charAt(0).toUpperCase() + p.label.slice(1),
              value: `${p.year}-${String(p.month).padStart(2, '0')}`,
            })),
            onChange: handleProductionPeriodChange,
          }}
        />

        {popoverOpen && (
          <div ref={popoverRef} className="absolute top-full left-8 z-50 -mt-2">
            <PipelinePopover
              pipelines={pipelines}
              selectedId={selectedId}
              onSelect={handleSelectPipeline}
              onEditPipeline={(pipeline) => {
                setEditingPipeline(pipeline);
                setShowEditModal(true);
              }}
              onEditStages={() => setDrawerOpen(true)}
              onClose={() => setPopoverOpen(false)}
            />
          </div>
        )}
      </div>

      <header className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
        <PipelineFilterBar
          search={search}
          onSearchChange={setSearch}
          view={boardView}
          onViewChange={setBoardView}
          summary={summary}
          pipelineTipo={selected?.tipo}
          filters={filterFields}
          activeFilters={activeFilters}
          onClearFilters={clearFilters}
        />

        {canManageStages && (
          <Tooltip title="Editar etapas da pipeline">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              disabled={!selectedId}
              aria-label="Editar etapas da pipeline"
              className="flex items-center justify-center w-9 h-9 text-gray-600 dark:text-gray-300 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            >
              <Settings2 className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
        <Segmented
          value={viewMode}
          onChange={(value) => setViewMode(value as 'kanban' | 'lista')}
          options={[
            {
              value: 'kanban',
              icon: <LayoutGrid className="w-4 h-4 inline-block align-[-3px]" />,
              title: 'Kanban',
            },
            {
              value: 'lista',
              icon: <List className="w-4 h-4 inline-block align-[-3px]" />,
              title: 'Lista',
            },
          ]}
          size="middle"
        />
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="flex-1 overflow-hidden p-4 min-w-0">
          {!selectedId || pipelines.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <Empty
                description={
                  pipelines.length === 0
                    ? 'Clique no nome da pipeline acima para criar seu primeiro funil'
                    : 'Selecione uma pipeline'
                }
              />
            </div>
          ) : viewMode === 'kanban' ? (
            <PipelineBoardKanban
              pipelineId={selectedId}
              embedded
              search={search}
              tagFilter={tagFilter}
              tenantFilter={tenantFilter}
              assembleiaVencimentos={effectiveAssembleiaVencimentos}
              grupoFilter={isPosVenda ? grupoFilter : ''}
              cotaFilter={isPosVenda ? cotaFilter : ''}
              boardView={boardView}
              onSummaryChange={handleSummaryChange}
              productionPeriodFilter={productionPeriodFilter}
              startDay={startDay}
            />
          ) : (
            <PipelineListView
              pipelineId={selectedId}
              search={search}
              tagFilter={tagFilter}
              tenantFilter={tenantFilter}
              assembleiaVencimentos={effectiveAssembleiaVencimentos}
              grupoFilter={isPosVenda ? grupoFilter : ''}
              cotaFilter={isPosVenda ? cotaFilter : ''}
              boardView={boardView}
              onSummaryChange={handleSummaryChange}
              productionPeriodFilter={productionPeriodFilter}
              startDay={startDay}
            />
          )}
        </div>
      </div>

      {selectedId && firstStage && (
        <PipelineNewCardModal
          open={newCardModalOpen}
          pipelineId={selectedId}
          stageId={firstStage.id}
          stageName={firstStage.nome}
          onClose={() => setNewCardModalOpen(false)}
          onCardCreated={() => {}}
        />
      )}

      <Drawer
        title="Etapas da Pipeline"
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        styles={{ body: { padding: 0 } }}
        width="380px"
      >
        {selectedId && <PipelineStagesEditor pipelineId={selectedId} />}
      </Drawer>

      <PipelineEditModal
        pipeline={editingPipeline}
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPipeline(null);
        }}
        onSuccess={() => {
          setShowEditModal(false);
          setEditingPipeline(null);
        }}
      />
    </div>
  );
}

export default SegmentedPipelineView;
